<script setup lang="ts">
import { computed } from 'vue'

import { formatDateTime, formatKey } from '@shared/utils'
import { vaspStatusColor } from '@shared/constants/vaspStatus'
import {
  transientFailureView,
  vaspFailureView,
  type VaspFailureView,
} from '@shared/constants/vaspFailure'
import type { VaspWalletDetails } from '../types/cryptoWalletTypes'
import {
  cryptoWalletStatusColor,
  walletValidationMethodLabel,
} from '../constants/cryptoWalletStatus'

import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Card from '@uiKit/components/Card.vue'
import IconBox from '@uiKit/components/IconBox.vue'
import InfoBanner from '@uiKit/components/InfoBanner.vue'
import StatusBadge from '@uiKit/components/StatusBadge.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'

const ID_SUFFIX_LENGTH = 6

const props = withDefaults(
  defineProps<{
    details: VaspWalletDetails
    /**
     * Message from the sync attempt that just failed in this session
     * (`toErrorMessage` output). Transient — outranks the persisted `lastError`,
     * which only appears once the refetch lands.
     */
    attemptError?: string | null
    /**
     * The wallet's status moved while the operator was looking at the old one, so
     * the sync was refused against a state they could not see. Swaps the banner's
     * advice from "try again" to "refresh".
     */
    stale?: boolean
  }>(),
  { attemptError: null, stale: false },
)

const emit = defineEmits<{ (e: 'refresh'): void }>()

// Source-of-truth freshness label (ux-principles §9). The timestamp is the
// registration's last Ctrl-Alt attempt: `investor_crypto_wallets` carries no sync
// column of its own, so admin-service records a wallet sync against the
// `vasp_users` row it belongs to.
const lastSyncedAt = computed(() => props.details.lastSyncedAt || props.details.validatedAt)

const sourceLabel = computed(() =>
  lastSyncedAt.value
    ? `Source: Ctrl-Alt v2 · Synced ${formatDateTime(lastSyncedAt.value)}`
    : 'Source: Ctrl-Alt v2',
)

// The just-failed attempt wins over the persisted one: admin-service recorded the
// same failure, but the refetch has not landed yet.
const failureBanner = computed<VaspFailureView | null>(() =>
  props.attemptError ? transientFailureView(props.attemptError) : vaspFailureView(props.details),
)

const failureTitle = computed(() =>
  props.stale ? 'This wallet changed while you were viewing it' : failureBanner.value?.title,
)

const sourceValue = computed(() => walletValidationMethodLabel(props.details.validationMethod))

// A wallet created by the crypto-readiness toggle has no registration behind it,
// so the whole VAISP section would render as a column of em-dashes. Say why
// instead — "no data" and "not applicable" are different answers to the operator.
const hasRegistration = computed(() => Boolean(props.details.vaspUserId))
</script>

<template>
  <Box flex direction="col" padding="4" gap="4" grow>
    <!-- Investor -->
    <Box flex direction="col" gap="0">
      <Text type="caption-sm" transform="uppercase" weight="semibold" class="section-label">
        Investor
      </Text>
      <Card variant="outlined" padding="0" class="detail-card">
        <Box flex align="center" gap="2" class="detail-card__head">
          <IconBox
            icon="user"
            size="sm"
            bg="var(--p-primary-50)"
            icon-color="var(--p-primary-600)"
          />
          <Box flex direction="col">
            <Text type="body-sm" weight="semibold">
              {{ `${details.firstName} ${details.lastName}`.trim() || '—' }}
            </Text>
            <Text type="caption" color="muted">{{ details.email || '—' }}</Text>
          </Box>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row--last">
          <Text type="body-sm" color="muted">Investor ID</Text>
          <Text type="caption" weight="medium" family="mono" class="detail-card__code">
            INV-{{ details.investorId.slice(-ID_SUFFIX_LENGTH).toUpperCase() }}
          </Text>
        </Box>
      </Card>
    </Box>

    <!-- Wallet -->
    <Box flex direction="col" gap="0">
      <Text type="caption-sm" transform="uppercase" weight="semibold" class="section-label">
        Wallet
      </Text>
      <Card variant="outlined" padding="0" class="detail-card">
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Wallet Status</Text>
          <StatusBadge
            :color="cryptoWalletStatusColor(details.walletStatus)"
            :label="formatKey(details.walletStatus)"
          />
        </Box>
        <Box flex justify="between" align="center" gap="2" class="detail-card__row">
          <Text type="body-sm" color="muted">Address</Text>
          <Box v-if="details.walletAddress" flex align="center" gap="2" class="detail-card__value">
            <Text type="caption" weight="medium" family="mono" truncate class="detail-card__code">
              {{ details.walletAddress }}
            </Text>
            <CopyButton :value="details.walletAddress" />
          </Box>
          <Text v-else type="body-sm" weight="medium">—</Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Network</Text>
          <Text type="body-sm" weight="medium">{{ details.blockchainNetwork || '—' }}</Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Origin</Text>
          <Text type="body-sm" weight="medium">{{ sourceValue }}</Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Primary Wallet</Text>
          <Text type="body-sm" weight="medium">{{ details.isPrimary ? 'Yes' : 'No' }}</Text>
        </Box>
        <Box flex justify="between" align="center" gap="2" class="detail-card__row">
          <Text type="body-sm" color="muted">VAISP Reference</Text>
          <Box
            v-if="details.vaspReferenceId"
            flex
            align="center"
            gap="2"
            class="detail-card__value"
          >
            <Text type="caption" weight="medium" family="mono" truncate class="detail-card__code">
              {{ details.vaspReferenceId }}
            </Text>
            <CopyButton :value="details.vaspReferenceId" />
          </Box>
          <Text v-else type="body-sm" weight="medium">—</Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Validated At</Text>
          <Text type="body-sm" weight="medium">
            {{ details.validatedAt ? formatDateTime(details.validatedAt) : '—' }}
          </Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row">
          <Text type="body-sm" color="muted">Created At</Text>
          <Text type="body-sm" weight="medium">
            {{ details.createdAt ? formatDateTime(details.createdAt) : '—' }}
          </Text>
        </Box>
        <Box flex justify="between" align="center" class="detail-card__row--last">
          <Text type="body-sm" color="muted">Last Updated</Text>
          <Text type="body-sm" weight="medium">
            {{ details.updatedAt ? formatDateTime(details.updatedAt) : '—' }}
          </Text>
        </Box>
      </Card>
    </Box>

    <!-- VAISP Registration — the organisation this wallet was minted against -->
    <Box flex direction="col" gap="0">
      <Text type="caption-sm" transform="uppercase" weight="semibold" class="section-label">
        VAISP Registration
      </Text>
      <template v-if="hasRegistration">
        <Text type="caption" color="muted" class="section-label">{{ sourceLabel }}</Text>
        <Card variant="outlined" padding="0" class="detail-card">
          <Box flex justify="between" align="center" class="detail-card__row">
            <Text type="body-sm" color="muted">Provider</Text>
            <Text type="body-sm" weight="medium" transform="capitalize">
              {{ details.vaspProvider || '—' }}
            </Text>
          </Box>
          <Box flex justify="between" align="center" class="detail-card__row">
            <Text type="body-sm" color="muted">Provider User ID</Text>
            <Text type="body-sm" weight="medium">{{ details.vaspUserId || '—' }}</Text>
          </Box>
          <Box flex justify="between" align="center" class="detail-card__row">
            <Text type="body-sm" color="muted">Participant Number</Text>
            <Text type="body-sm" weight="medium" family="mono">
              {{ details.participantNumber || '—' }}
            </Text>
          </Box>
          <Box flex justify="between" align="center" class="detail-card__row--last">
            <Text type="body-sm" color="muted">Registration Status</Text>
            <StatusBadge
              :color="vaspStatusColor(details.vaspUserStatus)"
              :label="details.vaspUserStatus ? formatKey(details.vaspUserStatus) : '—'"
            />
          </Box>
        </Card>
      </template>
      <Card v-else variant="outlined" padding="4">
        <Text type="body-sm" color="muted">
          No VAISP registration behind this wallet — it was created directly by an admin through
          crypto readiness, not by Ctrl-Alt. Sync is unavailable for these rows.
        </Text>
      </Card>
    </Box>

    <!-- Upstream failure of the last write attempt. Sits outside the Card: a
         tinted banner inside a key/value table reads as card-in-card
         (ux-principles §11), and the Card's overflow clip would square off its
         corners. Visually it still lands under the sections above. -->
    <InfoBanner
      v-if="failureBanner"
      :variant="stale ? 'warning' : 'danger'"
      :icon="stale ? 'clock' : 'exclamation-triangle'"
      :title="failureTitle"
    >
      <!-- InfoBanner already wraps this slot in a <Text type="body-sm">, which
           renders a <p> — so the meta line has to be phrasing content. A second
           body-sm would nest <p> in <p> and silently break the layout. -->
      <template #default>
        {{ failureBanner.message
        }}<template v-if="failureBanner.meta"
          ><br /><Text type="caption">{{ failureBanner.meta }}</Text></template
        >
      </template>
      <template v-if="stale" #actions>
        <!-- Refresh, not Retry: the sync was refused against a state this drawer
             is not showing, so the same action would fail identically. -->
        <Button
          label="Refresh"
          icon="refresh"
          color="secondary"
          variant="outline"
          size="small"
          @click="emit('refresh')"
        />
      </template>
    </InfoBanner>
  </Box>
</template>

<style scoped>
.section-label {
  margin-bottom: 0.5rem;
}

.detail-card {
  overflow: hidden;
}

.detail-card__head {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--p-surface-100);
}

.detail-card__row {
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--p-surface-100);
}

.detail-card__row--last {
  padding: 0.625rem 1rem;
}

/* Caps the code chip so a long XRPL address truncates instead of pushing the
   copy button off the 480px drawer. */
.detail-card__value {
  min-width: 0;
  max-width: 260px;
}

.detail-card__code {
  background: var(--p-surface-50);
  border: 1px solid var(--p-surface-200);
  border-radius: var(--p-border-radius-sm);
  padding: 2px 7px;
}
</style>
